import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  useGetContractsExpirationDetailsQuery,
  useGetContractsExpirationSummaryQuery,
  useLazyGetContractsExpirationExportDetailsQuery,
} from '@client/api';
import { Card, exportToExcel, Pagination, Typography, useRouteDocumentTitle } from '@client/toolkit';
import { FaCalendarAlt } from 'react-icons/fa';
import { ReportingPaths } from '../reporting.router';
import { ExpiringContractCard } from './components/expiring-contract-card';
import { ExpiringContractTable } from './components/expiring-contract-table';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import { dateRangeOptions } from '../utils/date-range-options';
import { DateRangeFilter } from '../components/date-range-filter';
import dayjs from 'dayjs';
import { transformToExcelFormat } from './utils/transformToExcelFormat';
import { SortDirection } from '../components/expanable-table-wraper';

export function ReportingExpireContactRoute() {
  const { t } = useTranslation('reporting');
  const { t: tGlobal, i18n } = useTranslation('global');
  const [searchParams] = useSearchParams();

  const startDateParam = searchParams.get('startDate');
  const endDateParam = searchParams.get('endDate');

  const initialStartDate = startDateParam
    ? dayjs(startDateParam).toDate()
    : dateRangeOptions().nextTwelveMonths.startDate;
  const initialEndDate = endDateParam ? dayjs(endDateParam).toDate() : dateRangeOptions().nextTwelveMonths.endDate;

  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('BuildingName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [timeRange, setTimeRange] = useState<{ startDate: Date; endDate: Date }>({
    startDate: initialStartDate,
    endDate: initialEndDate,
  });
  useRouteDocumentTitle(t('reporting.documentTitle'), ReportingPaths.ExpireContract);

  const { data: summaryData, isFetching: isFetchingSummary } = useGetContractsExpirationSummaryQuery(
    {
      startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
    },
    {
      skip: !timeRange.startDate || !timeRange.endDate,
    },
  );
  const { data: detailsData, isFetching: isFetchingDetails } = useGetContractsExpirationDetailsQuery(
    {
      startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
      orderBy: `${sortColumn}:${sortDirection}`,
      page: currentPage,
      pageSize: 10,
    },
    {
      skip: !timeRange.startDate || !timeRange.endDate,
    },
  );
  const [trigger] = useLazyGetContractsExpirationExportDetailsQuery();
  const onExportExcel = useCallback(async () => {
    setExporting(true);
    const result = await trigger({
      startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
      pageSize: 0,
    });
    if (result.isSuccess) {
      exportToExcel(transformToExcelFormat(result.data, selectedRows), 'contracts-expiration-details.xlsx');
    }
    setExporting(false);
  }, [timeRange, trigger, selectedRows]);

  const buckets = useMemo(() => summaryData?.buckets ?? [], [summaryData?.buckets]);

  const dateLabel = useMemo(() => {
    if (timeRange.startDate && timeRange.endDate) {
      return `${dayjs(timeRange.startDate).format('DD.MM.YYYY')} - ${dayjs(timeRange.endDate).format('DD.MM.YYYY')}`;
    }
    return dateRangeOptions().nextTwelveMonths.displayLabel;
  }, [timeRange]);

  return (
    <ReportPageContent
      breadcrumb={tGlobal('navigation.reporting.expireContract')}
      menu={
        <DateRangeFilter
          options={[dateRangeOptions().nextTwelveMonths, dateRangeOptions().custom]}
          onChange={(dateRange) => setTimeRange({ startDate: dateRange[0], endDate: dateRange[1] })}
          onExportExcel={onExportExcel}
          exporting={exporting}
          initialStartDate={timeRange.startDate}
          initialEndDate={timeRange.endDate}
        />
      }
    >
      <div className="flex gap-5 justify-between h-[146px] text-white">
        {buckets.map((bucket, index) => (
          <ExpiringContractCard
            key={bucket.label}
            title={bucket.label}
            value={bucket.count}
            isCritical={bucket.isCritical}
            loading={isFetchingSummary}
          />
        ))}
        {buckets.length === 0 && !isFetchingSummary && (
          <Typography className="text-gray-400">{t('noDataAvailable')}</Typography>
        )}
      </div>
      <div>
        <span className="flex items-baseline gap-2 mb-4 text-sm">
          <FaCalendarAlt className="text-white" />
          <Typography className="text-white text-sm">{dateLabel}</Typography>
        </span>
        <Card className="mb-4">
          <ExpiringContractTable
            data={detailsData?.items ?? []}
            loading={isFetchingDetails}
            onSelected={(ids) => {
              setSelectedRows(ids);
            }}
            onSort={(col, direction) => {
              setSortColumn(col);
              setSortDirection(direction);
              setCurrentPage(0);
            }}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
          />
          <Pagination
            totalCount={detailsData?.total ?? 0}
            currentPage={currentPage}
            setPage={setCurrentPage}
            pageSize={detailsData?.pageSize ?? 10}
            visible
          />
        </Card>
      </div>
    </ReportPageContent>
  );
}
